import { Route, Switch } from 'wouter';
import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy, type ComponentType, type ReactNode } from 'react';
import { queryClient } from '@/lib/queryClient';
import Navbar from '@/components/layout/navbar';
import Footer from '@/components/layout/footer';
import { Toaster } from '@/components/ui/toaster';
import { GradientBackground } from '@/components/ui/gradient-background';
import '@/index.css';
import { ScrollToTop } from '@/components/ScrollToTop';
import { StructuredData } from '@/components/seo';
import { PageTracker } from '@/components/PageTracker';
import { CookieConsent } from '@/components/CookieConsent';

// Import critical pages directly (home, contact)
import HomePage from '@/pages/home-page';
import ContactPage from '@/pages/contact';
import NotFound from '@/pages/not-found';

// Lazy load everything else. Keep this list in sync with SPA_ROUTE and PAGE_SEO in the Worker.
const routes: Array<[string, ComponentType]> = [
  ['/services', lazy(() => import('@/pages/services/services-page'))],
  ['/principal', lazy(() => import('@/pages/principal'))],
  ['/blog', lazy(() => import('@/pages/blog/hashnode'))],
  [
    '/contact/client/build',
    lazy(() => import('@/pages/contact/client/dev-services/contact-client-build-page')),
  ],
  [
    '/contact/partner-network',
    lazy(() => import('@/pages/contact/partner/contact-partner-network')),
  ],
  ['/contact/general', lazy(() => import('@/pages/contact/general'))],
  ['/contact/advisory', lazy(() => import('@/pages/contact/client/advisory'))],
  ['/contact/confirmation/:type', lazy(() => import('@/pages/contact/confirmation'))],
  ['/privacy', lazy(() => import('@/pages/privacy'))],
  ['/terms', lazy(() => import('@/pages/terms'))],
];

// Every lazy route shares the same blank full-height fallback.
function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen" />}>
      {children}
    </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PageTracker />
      <ScrollToTop />
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <div className="min-h-screen flex flex-col">
        <GradientBackground />
        <Navbar />
        <main className="flex-1 flex flex-col">
          <Switch>
            <Route path="/">
              {() => (
                <Lazy>
                  <HomePage />
                </Lazy>
              )}
            </Route>
            <Route path="/contact">{() => <ContactPage />}</Route>
            {routes.map(([path, Page]) => (
              <Route key={path} path={path}>
                {() => (
                  <Lazy>
                    <Page />
                  </Lazy>
                )}
              </Route>
            ))}
            <Route>{() => <NotFound />}</Route>
          </Switch>
        </main>
        <Footer />
      </div>
      <Toaster />
      <CookieConsent />
    </QueryClientProvider>
  );
}

export default App;
